import {
  BANNER_CONTENT_HIDDEN_CLASS,
  HEADER_LOADER_REVEALED_CLASS,
  resetLoaderBannerWithoutAnimation,
  runLoaderBannerReveal,
  setLoaderBannerInitialState,
} from "./loader-banner-reveal.js";
import {
  prepareBannerMediaForLoader,
  startBannerMediaPlaybackWatch,
  teardownBannerMediaAfterLoader,
  waitForBannerMediaPlaying,
} from "./loader-banner-media.js";

/** Loader paints → brief pause → subtitle fade in → hold until video plays → fade out → banner reveal. */
const SUBTITLE_FADE_IN_DELAY_S = 0.6;
const SUBTITLE_FADE_IN_S = 0.6;
/** Fallback hold when there is no banner video (timed sequence). */
const SUBTITLE_HOLD_S = 2.5;
/** Seconds after subtitle fades in before it fades out. */
const SUBTITLE_FADE_OUT_S = 0.3;
/** Seconds after video starts playing before the subtitle fades out. */
const SUBTITLE_HIDE_AFTER_PLAY_S = 2.5;
/** Seconds after video starts playing before the loader is hidden. */
const LOADER_HIDE_AFTER_PLAY_S = 2.8;
/** Safety net for playback that never starts (blocked autoplay, dead network) — not a normal wait. */
const MEDIA_PLAY_WAIT_MAX_S = 20;

const DEFAULT_OPTIONS = {
  removeLoaderNode: false,
};

function getGsap() {
  return typeof window !== "undefined" && window.gsap ? window.gsap : null;
}

/**
 * @param {object} [options]
 * @param {boolean} [options.removeLoaderNode=false] Remove `#site-loader` from the DOM after the loader is hidden.
 */
export function initSiteLoader(options = {}) {
  const { removeLoaderNode } = { ...DEFAULT_OPTIONS, ...options };
  const gsap = getGsap();

  startBannerMediaPlaybackWatch();

  const prefersReducedMotion = () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let subtitleShown = false;

  /**
   * The subtitle comes in with the loader itself rather than at `window.load`, which a fast
   * (or cached) banner video can beat — the text would then only appear once the video is playing.
   */
  const showSubtitle = () => {
    if (subtitleShown) return;

    const loaderText = document.querySelector("#site-loader .loader-text");
    if (!loaderText) return;

    subtitleShown = true;
    document.documentElement.classList.add("site-loader-active");

    if (!gsap) {
      window.setTimeout(() => {
        loaderText.style.opacity = "1";
      }, prefersReducedMotion() ? 0 : SUBTITLE_FADE_IN_DELAY_S * 1000);
      return;
    }

    gsap.set(loaderText, { opacity: 0 });
    gsap.to(loaderText, {
      opacity: 1,
      delay: prefersReducedMotion() ? 0 : SUBTITLE_FADE_IN_DELAY_S,
      duration: prefersReducedMotion() ? 0 : SUBTITLE_FADE_IN_S,
      ease: "power1.inOut",
    });
  };

  const runSequence = () => {
    const loader = document.getElementById("site-loader");
    if (!loader) return;

    const inner = loader.querySelector(".site-loader__inner");
    const loaderText = loader.querySelector(".loader-text");

    document.documentElement.classList.add("site-loader-active");

    const bannerMedia = prepareBannerMediaForLoader();
    const usesBannerMedia = Boolean(bannerMedia);

    if (usesBannerMedia) {
      document.documentElement.classList.add("site-loader-uses-banner-media");
      loader.classList.add("site-loader--banner-media");
    }

    const prefersReduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const bannerEls = gsap ? setLoaderBannerInitialState(gsap) : null;

    const removeLoader = () => {
      if (removeLoaderNode && loader.parentNode) loader.remove();
    };

    const afterBannerReveal = (next) => {
      if (!bannerEls || !gsap) {
        next();
        return;
      }
      runLoaderBannerReveal(gsap, { prefersReduced, onComplete: next });
    };

    const cleanupLoaderState = () => {
      teardownBannerMediaAfterLoader(bannerMedia);
      document.documentElement.classList.remove("site-loader-uses-banner-media");
    };

    if (!gsap) {
      loader.classList.add("is-hidden");
      loader.setAttribute("aria-busy", "false");
      document.body.classList.add("site-loader-hidden");
      document.documentElement.classList.remove("site-loader-active");
      resetLoaderBannerWithoutAnimation();
      cleanupLoaderState();
      removeLoader();
      return;
    }

    const finishHide = () => {
      loader.classList.add("is-hidden");
      loader.setAttribute("aria-busy", "false");
      document.body.classList.add("site-loader-hidden");
      document.documentElement.classList.remove("site-loader-active");

      gsap.killTweensOf(
        [
          loader,
          inner,
          loaderText,
          bannerEls?.section,
          bannerEls?.container,
          bannerEls?.overlay,
          bannerEls?.header,
        ].filter(Boolean)
      );
      if (bannerEls) {
        bannerEls.section.classList.remove("banner--loader-pending");
        bannerEls.container.classList.remove(BANNER_CONTENT_HIDDEN_CLASS);
        gsap.set(bannerEls.overlay, { clearProps: "opacity" });
        if (
          bannerEls.header &&
          document.body.classList.contains("has-loader")
        ) {
          bannerEls.header.classList.add(HEADER_LOADER_REVEALED_CLASS);
        }
      }
      cleanupLoaderState();
      removeLoader();
    };

    /** Hide the text overlay immediately, then run the banner reveal. */
    const runExit = (onComplete) => {
      gsap.killTweensOf([loader, inner, loaderText].filter(Boolean));
      if (loaderText) {
        gsap.set(loaderText, { opacity: 0 });
      }
      gsap.set(loader, { autoAlpha: 0 });
      onComplete?.();
    };

    const runBannerAfterLoaderExit = () => {
      runExit(() => afterBannerReveal(finishHide));
    };

    const placeholderHoldBeforeExit =
      SUBTITLE_FADE_IN_DELAY_S +
      SUBTITLE_FADE_IN_S +
      SUBTITLE_HOLD_S +
      SUBTITLE_FADE_OUT_S;

    const introHoldPlaceholder = () => {
      gsap
        .timeline()
        .to({}, { duration: prefersReduced ? 0.35 : placeholderHoldBeforeExit })
        .call(runBannerAfterLoaderExit);
    };

    const fadeOutSubtitle = () => {
      if (!loaderText) return;
      gsap.to(loaderText, {
        opacity: 0,
        duration: prefersReduced ? 0 : SUBTITLE_FADE_OUT_S,
        ease: "power1.inOut",
      });
    };

    /** Both hides are measured from the moment playback starts. */
    const scheduleHideFromPlay = () => {
      gsap.delayedCall(SUBTITLE_HIDE_AFTER_PLAY_S, fadeOutSubtitle);
      gsap.delayedCall(LOADER_HIDE_AFTER_PLAY_S, runBannerAfterLoaderExit);
    };

    /** No banner video to wait on: hold the subtitle for a fixed time instead. */
    const scheduleTimedHide = () => {
      gsap.delayedCall(SUBTITLE_HOLD_S, fadeOutSubtitle);
      gsap.delayedCall(
        SUBTITLE_HOLD_S + (prefersReduced ? 0 : SUBTITLE_FADE_OUT_S),
        runBannerAfterLoaderExit
      );
    };

    const runTextIntro = () => {
      showSubtitle();

      if (usesBannerMedia && bannerMedia) {
        waitForBannerMediaPlaying(bannerMedia, {
          timeoutMs: MEDIA_PLAY_WAIT_MAX_S * 1000,
        }).then(scheduleHideFromPlay);
        return;
      }

      if (!loaderText) {
        introHoldPlaceholder();
        return;
      }

      scheduleTimedHide();
    };

    runTextIntro();
  };

  showSubtitle();
  if (!subtitleShown) {
    document.addEventListener("DOMContentLoaded", showSubtitle, { once: true });
  }

  if (document.readyState === "complete") {
    runSequence();
    return;
  }

  window.addEventListener("load", runSequence, { once: true });
}
